import React from 'react'
import { Icon } from './icons'
import { fmtMB, fmtUptime } from './format'
import { Avatar, Bar, MCAvatar, StatusDot } from './shared'
import type { LifecycleAction, LogLine, Role, Server, StatusDotKind, ToastPush, User } from './types'

interface TabDef {
  id: string
  label: string
  phase?: string
}

export const TABS: TabDef[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'console', label: 'Console' },
  { id: 'quick', label: 'Quick Actions' },
  { id: 'players', label: 'Players' },
  { id: 'properties', label: 'Properties', phase: 'P2' },
  { id: 'plugins', label: 'Plugins', phase: 'P2' },
]

function statusKindOf(server: Server): StatusDotKind {
  if (server.status === 'online') return 'online'
  if (server.status === 'hibernating' || server.status === 'starting' || server.status === 'stopping' || server.status === 'restarting') return 'warn'
  return 'offline'
}

function statusLabelOf(server: Server): string {
  if (server.status === 'online') return 'Online'
  if (server.status === 'hibernating') return 'Hibernating'
  if (server.status === 'starting') return 'Starting'
  if (server.status === 'stopping') return 'Stopping'
  if (server.status === 'restarting') return 'Restarting'
  return 'Offline'
}

function ramUsagePct(server: Server): number {
  if (server.ramMaxMb <= 0) return 0
  return (server.ramMb / server.ramMaxMb) * 100
}

interface ServerHeaderProps {
  server: Server
  tab: string
  setTab: (tab: string) => void
}

export function ServerHeader({ server, tab, setTab }: ServerHeaderProps) {
  const statusKind = statusKindOf(server)
  const statusLabel = statusLabelOf(server)
  return (
    <>
      <div className="page-header" data-screen-label={`Server ${server.name}`}>
        <StatusDot kind={statusKind} />
        <h1 className="mono">{server.name}</h1>
        <span className="subtitle">
          {statusLabel} - v{server.version}
          {server.status === 'online' && ` - ${server.players.length} players - ${server.tps !== null ? server.tps.toFixed(1) : '--'} TPS`}
        </span>
        <div className="header-right">
          {server.operation && <span className="chip accent">{server.operation.label}</span>}
          <span className="chip">{server.unit}</span>
          <span className="chip">:{server.port}</span>
        </div>
      </div>
      <div className="tabs">
        {TABS.map(item => (
          <div
            key={item.id}
            className={`tab ${tab === item.id ? 'active' : ''} ${item.phase ? 'greyed' : ''}`}
            onClick={() => { if (!item.phase) setTab(item.id) }}
          >
            {item.label}
            {item.phase && <span className="phase-badge">{item.phase}</span>}
          </div>
        ))}
      </div>
    </>
  )
}

interface OverviewTabProps {
  server: Server
  role: Role
  users: User[]
  capabilities?: {
    canDownloadWorld: boolean
    canDownloadLogs: boolean
  }
  onAction: (action: LifecycleAction, server: Server) => void
}

export function OverviewTab({ server, role, users, capabilities, onAction }: OverviewTabProps) {
  const canLifecycle = role === 'owner' || role === 'admin'
  const canDownloadWorld = capabilities?.canDownloadWorld ?? false
  const canDownloadLogs = capabilities?.canDownloadLogs ?? false
  const statusKind = statusKindOf(server)
  const ramPct = ramUsagePct(server)

  return (
    <div className="split-2">
      <div className="flex-col gap-4">
        <div className="card">
          <div className="flex items-center gap-2 mb-3">
            <h3 style={{ margin: 0 }}>Status</h3>
            <StatusDot kind={statusKind} />
          </div>
          <div className="grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', gap: 18 }}>
            <div><div className="text-xs dim">TPS</div><div className="mono" style={{ fontSize: 18, fontWeight: 600 }}>{server.tps !== null ? server.tps.toFixed(1) : '--'}</div></div>
            <div><div className="text-xs dim">Uptime</div><div className="mono" style={{ fontSize: 18, fontWeight: 600 }}>{server.status === 'online' ? fmtUptime(server.uptimeSec) : server.status === 'hibernating' ? 'warm on join' : '--'}</div></div>
            <div><div className="text-xs dim">Online</div><div className="mono" style={{ fontSize: 18, fontWeight: 600 }}>{server.players.length} / {server.maxPlayers}</div></div>
            <div><div className="text-xs dim">Version</div><div className="mono" style={{ fontSize: 18, fontWeight: 600 }}>{server.version}</div></div>
          </div>
        </div>

        <div className="card">
          <h3 className="mb-3">Online players</h3>
          {server.players.length ? server.players.map(player => {
            const user = users.find(item => item.mcUsername === player)
            return (
              <div key={player} className="player-row">
                <MCAvatar name={player} size="sm" />
                <span className="pname">{player}</span>
                {user && <span className="chip" style={{ marginLeft: 10 }}><Avatar user={user} size={14} /> {user.displayName}</span>}
              </div>
            )
          }) : (
            <div className="dim text-sm" style={{ padding: '20px 0', textAlign: 'center' }}>
              <Icon name="users" size={22} style={{ opacity: 0.5, display: 'block', margin: '0 auto 6px' }} />
              No players online
            </div>
          )}
        </div>
      </div>

      <div className="flex-col gap-4">
        {server.operation && (
          <div className="banner info">
            <Icon name="info" size={14} style={{ marginTop: 1 }} />
            <div>
              <div style={{ fontWeight: 500 }}>{server.operation.label}</div>
              <div className="text-xs" style={{ opacity: 0.8 }}>Requested at {server.operation.startedAt.replace('T', ' ').replace('Z', '')}</div>
            </div>
          </div>
        )}
        {server.restartPending && (
          <div className="banner warn">
            <Icon name="alert-triangle" size={14} style={{ marginTop: 1 }} />
            <div>
              <div style={{ fontWeight: 500 }}>Restart pending</div>
              <div className="text-xs" style={{ opacity: 0.8 }}>Config changes will apply after the next restart.</div>
            </div>
          </div>
        )}
        {canLifecycle && (
          <div className="card">
            <h3 className="mb-3">Lifecycle</h3>
            {server.status === 'online' ? (
              <div className="flex-col gap-2">
                <button className="btn" onClick={() => onAction('restart', server)} disabled={Boolean(server.operation)}>
                  <Icon name="rotate-ccw" size={13} /> Restart {server.name}
                </button>
                <button className="btn danger" onClick={() => onAction('stop', server)} disabled={Boolean(server.operation)}>
                  <Icon name="square" size={13} /> Stop {server.name}
                </button>
              </div>
            ) : (
              <button className="btn primary" onClick={() => onAction('start', server)} disabled={Boolean(server.operation)}>
                <Icon name="play" size={13} /> Start {server.name}
              </button>
            )}
            <div className="text-xs dim mt-3">
              Destructive actions open a confirm dialog with a 30s countdown.
            </div>
          </div>
        )}
        {(canDownloadWorld || canDownloadLogs) && (
          <div className="card">
            <h3 className="mb-3">Downloads</h3>
            <div className="flex-col gap-2">
              {canDownloadWorld && (
                <a className="btn" href={`/api/servers/${server.id}/world/archive`} download>
                  <Icon name="download" size={13} /> Download latest world archive
                </a>
              )}
              {canDownloadLogs && (
                <a className="btn ghost" href={`/api/servers/${server.id}/logs/archive`} download>
                  <Icon name="archive" size={13} /> Download rotated logs (zip)
                </a>
              )}
            </div>
            <div className="text-xs dim mt-2">
              World requires a backup &lt; 24h old. Logs include <span className="mono">*.gz</span> only (never <span className="mono">latest.log</span>).
            </div>
          </div>
        )}
        <div className="card">
          <h3 className="mb-3">Process resources</h3>
          <div className="flex-col gap-3">
            <div>
              <div className="flex justify-between text-xs" style={{ marginBottom: 4 }}>
                <span className="dim">CPU</span>
                <span className="mono">{server.cpu}%</span>
              </div>
              <Bar pct={server.cpu} color="var(--accent-400)" />
            </div>
            <div>
              <div className="flex justify-between text-xs" style={{ marginBottom: 4 }}>
                <span className="dim">RAM</span>
                <span className="mono">{fmtMB(server.ramMb)}{server.ramMaxMb > 0 && ` / ${fmtMB(server.ramMaxMb)}`}</span>
              </div>
              <Bar pct={ramPct} color="var(--emerald-400)" />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

interface ConsoleTabProps {
  server: Server
  role: Role
  logs: LogLine[]
  onSendCommand: (input: string) => void
  onClear: () => void
  toast: ToastPush
}

export function ConsoleTab({ server, role, logs, onSendCommand, onClear, toast }: ConsoleTabProps) {
  const [cmd, setCmd] = React.useState('')
  const [autoscroll, setAutoscroll] = React.useState(true)
  const [chatOnly, setChatOnly] = React.useState(role === 'player')
  const bodyRef = React.useRef<HTMLDivElement | null>(null)

  React.useEffect(() => {
    setChatOnly(role === 'player')
  }, [role])

  const visible = React.useMemo(
    () => logs.filter(line => !chatOnly || line.kind === 'chat'),
    [logs, chatOnly],
  )

  // Stable signature for autoscroll: depends on what the user actually sees,
  // so idle re-polls and backend-filtered noise don't drag the view down.
  const visibleSignature = React.useMemo(() => {
    if (visible.length === 0) return '0|'
    const last = visible[visible.length - 1]
    return `${visible.length}|${last.t}|${last.msg ?? ''}|${last.who ?? ''}`
  }, [visible])

  React.useEffect(() => {
    if (autoscroll && bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight
    }
  }, [autoscroll, visibleSignature])

  // iOS Safari keyboard handling: track the on-screen keyboard's vertical
  // offset via visualViewport so the sticky console footer floats above it.
  React.useEffect(() => {
    const vv = window.visualViewport
    if (!vv) return
    const onResize = () => {
      const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop)
      document.documentElement.style.setProperty('--kb-inset', `${inset}px`)
      if (autoscroll && bodyRef.current) {
        bodyRef.current.scrollTop = bodyRef.current.scrollHeight
      }
    }
    vv.addEventListener('resize', onResize)
    vv.addEventListener('scroll', onResize)
    return () => {
      vv.removeEventListener('resize', onResize)
      vv.removeEventListener('scroll', onResize)
      document.documentElement.style.removeProperty('--kb-inset')
    }
  }, [autoscroll])

  const canSend = role === 'owner' || role === 'admin'

  async function copyVisibleLines() {
    const content = visible.map(line => {
      const message = line.kind === 'chat' ? `<${line.who}> ${line.msg}` : line.msg
      return `[${line.t}] ${line.lvl} ${message ?? ''}`.trim()
    }).join('\n')

    if (!content) {
      toast({ variant: 'warn', title: 'Nothing to copy' })
      return
    }

    try {
      await navigator.clipboard.writeText(content)
      toast({ variant: 'success', title: 'Console copied', sub: `${visible.length} lines` })
    } catch {
      toast({ variant: 'warn', title: 'Copy failed', sub: 'Clipboard permission was denied.' })
    }
  }

  function handleSend(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!cmd.trim()) return
    if (!canSend) {
      toast({ variant: 'warn', title: 'Raw RCON blocked', sub: 'Use Quick Actions - raw RCON is admin/owner only.' })
      return
    }
    onSendCommand(cmd)
    setCmd('')
  }

  if (server.status !== 'online') {
    return (
      <div className="card" style={{ textAlign: 'center', padding: '48px 20px' }}>
        <Icon name="power" size={28} style={{ opacity: 0.4, marginBottom: 10 }} />
        <h3 style={{ margin: '0 0 4px' }}>{server.name} is {statusLabelOf(server).toLowerCase()}</h3>
        <div className="dim text-sm mb-3">
          {server.msh ? 'msh will warm the JVM when a player connects - or start it manually.' : 'Start the server to see its console.'}
        </div>
      </div>
    )
  }

  return (
    <div className="console-shell">
      <div className="console-toolbar">
        <label><input type="checkbox" checked={chatOnly} onChange={event => setChatOnly(event.target.checked)} /> chat only</label>
        <label><input type="checkbox" checked={autoscroll} onChange={event => setAutoscroll(event.target.checked)} /> autoscroll</label>
        <div className="ml-auto flex gap-2">
          <button className="btn sm ghost" onClick={() => { void copyVisibleLines() }}><Icon name="copy" size={11} /> Copy</button>
          <button className="btn sm ghost" onClick={onClear}>Clear view</button>
        </div>
      </div>
      <div className="console-body" ref={bodyRef} onWheel={event => {
        const element = event.currentTarget
        if (element.scrollTop + element.clientHeight < element.scrollHeight - 40) setAutoscroll(false)
      }}>
        {visible.map((line, index) => (
          <div key={`${line.t}-${index}`} className={`log-line ${line.kind}`}>
            <span className="t">[{line.t}]</span>
            <span className="lvl">{line.lvl}</span>
            <span className="msg">
              {line.kind === 'chat' ? <><span className="name">&lt;{line.who}&gt;</span> {line.msg}</> : line.msg}
            </span>
          </div>
        ))}
        {visible.length === 0 && <div className="dim" style={{ padding: 20, textAlign: 'center' }}>No lines match the current filter.</div>}
      </div>
      <form className="console-footer" onSubmit={handleSend}>
        <input
          placeholder={canSend ? 'Send raw RCON command...' : 'Console is read-only for this role. Use Quick Actions.'}
          value={cmd}
          disabled={!canSend}
          onChange={event => setCmd(event.target.value)}
        />
        <button className="btn primary" type="submit" disabled={!canSend || !cmd.trim()}><Icon name="send" size={12} /> Send</button>
      </form>
    </div>
  )
}
